// 번역 라이브러리(Tolgee) 어댑터입니다. 번역 라이브러리를 직접 import 하는 유일한 파일이며
// (.oxlintrc.json 의 no-restricted-imports 가 강제), 앱 코드는 여기서 내보내는 API 만 씁니다.
// 라이브러리를 바꿀 때는 이 파일의 공개 API 시그니처만 유지하면 됩니다.
import { FormatIcu } from '@tolgee/format-icu';
import {
  DevTools,
  GlobalContextPlugin,
  T,
  Tolgee,
  type TolgeeStaticData,
  useTolgee,
  useTranslate,
} from '@tolgee/react';
import {
  type ReactElement,
  type ReactNode,
  createElement,
  useCallback,
} from 'react';
import { create } from 'zustand';

import { APP_CONST } from '@nenana/util/app/constant';
import type en from '@nenana/util/locales/en.json';

export type TranslationKey = keyof typeof en;

export type TranslationParamValue = Nullish<
  bigint | boolean | Date | number | string
>;

export type TranslationParams = Record<string, TranslationParamValue>;

// 서버 값 등 동적 키도 받도록 string 을 허용하고, 리터럴은 TranslationKey 로 자동완성됩니다.
export type TranslateFn = (
  key: TranslationKey | (string & {}),
  params?: TranslationParams,
) => string;

export type TransParamValue =
  | ReactElement
  | TranslationParamValue
  | ((chunk: ReactNode) => ReactElement | null);

type TransProps = {
  i18nKey: TranslationKey;
  params?: Record<string, TransParamValue>;
};

const DEFAULT_LANGUAGE = APP_CONST.LANG.EN;

const LANGUAGE_LIST = Object.values(APP_CONST.LANG);

const toSupportedLanguage = (language: Nullish<string>): APP_CONST.LANG =>
  LANGUAGE_LIST.find(item => item === language) ?? DEFAULT_LANGUAGE;

// 번역 JSON 은 같은 오리진의 언어별 청크로 지연 로드합니다. 외부 요청이 없어 내부망(오프라인)에서도 동작하며,
// glob 이라 번역 파일이 없는 worktree/테스트에서도 import 오류가 나지 않습니다.
const LOCALE_STATIC_DATA: TolgeeStaticData = Object.fromEntries(
  Object.entries(
    // 앱 폴더마다 Vite 루트가 달라질 수 있어 이 파일 기준 상대 경로로 찾습니다.
    import.meta.glob<Record<string, string>>('../../locales/*.json', {
      import: 'default',
    }),
  ).map(([path, load]) => [path.replace(/^.*\/(\w+)\.json$/, '$1'), load]),
);

// 사이트별 번역은 Tolgee namespace 로 pull 되어 packages/util/src/locales/<사이트>/<언어>.json 에 있으며, 기본 번역(ns '')을 덮어쓸 키만 담습니다.
// Tolgee staticData 키 '<언어>:<사이트>' 로 바꿔 등록합니다.
const SITE_STATIC_DATA: TolgeeStaticData = Object.fromEntries(
  Object.entries(
    import.meta.glob<Record<string, string>>('../../locales/*/*.json', {
      eager: true,
      import: 'default',
    }),
  ).map(([path, data]) => [
    path.replace(/^.*\/locales\/([^/]+)\/(\w+)\.json$/, '$2:$1'),
    data,
  ]),
);

const toSiteNamespaceList = (staticData: TolgeeStaticData) => [
  ...new Set(
    Object.keys(staticData)
      .filter(key => key.includes(':'))
      .map(key => key.split(':')[1]),
  ),
];

const SITE_NAMESPACE_LIST = toSiteNamespaceList(SITE_STATIC_DATA);

// initI18n 이 받은 사이트 번역 기준으로 갱신됩니다. 기본값은 번들된 사이트 목록입니다.
let siteNamespaceList = SITE_NAMESPACE_LIST;

const useSiteNamespaceStore = create<{ ns: string }>(() => ({ ns: '' }));

// 번들에 없는 사이트명이나 문자열이 아닌 값은 기본 번역('')으로 되돌립니다.
export const setSiteNamespace = (ns: unknown) =>
  useSiteNamespaceStore.setState({
    ns: typeof ns === 'string' && siteNamespaceList.includes(ns) ? ns : '',
  });

// in-context 편집(Alt + 클릭) 자격 증명은 dev 서버 전용입니다. DEV 가드로 production 번들에서 제거되며,
// 런타임 env(runtime-env.js)는 읽지 않습니다.
const inContextCredential =
  import.meta.env.DEV &&
  import.meta.env.MODE !== 'test' &&
  import.meta.env.VITE_TOLGEE_API_KEY &&
  import.meta.env.VITE_TOLGEE_API_URL
    ? {
        apiKey: import.meta.env.VITE_TOLGEE_API_KEY,
        apiUrl: import.meta.env.VITE_TOLGEE_API_URL,
      }
    : null;

const tolgeeChain = Tolgee()
  .use(GlobalContextPlugin({ useSuspense: false }))
  .use(FormatIcu());

// DevTools 는 개발 빌드에서 자격 증명이 없어도 문자열에 보이지 않는 표시 문자를 붙이므로
// in-context 편집을 쓸 때만 붙입니다.
const tolgee = (
  inContextCredential ? tolgeeChain.use(DevTools()) : tolgeeChain
).init({
  ...inContextCredential,
  availableLanguages: LANGUAGE_LIST,
  defaultLanguage: toSupportedLanguage(
    navigator.language.replace(/-\w+/gi, '').toLowerCase(),
  ),
  fallbackLanguage: DEFAULT_LANGUAGE,
  // 사이트 번역에 없는 키는 기본 번역으로 대체합니다.
  fallbackNs: [''],
});

export const initI18n = (
  staticData: TolgeeStaticData = LOCALE_STATIC_DATA,
  siteStaticData: TolgeeStaticData = SITE_STATIC_DATA,
) => {
  siteNamespaceList = toSiteNamespaceList(siteStaticData);
  tolgee.addStaticData({ ...staticData, ...siteStaticData });

  return tolgee.run();
};

export const changeLanguage = async (language: string) => {
  await tolgee.changeLanguage(toSupportedLanguage(language));
};

export const getLanguage = () => toSupportedLanguage(tolgee.getLanguage());

export const useTranslation = () => {
  const ns = useSiteNamespaceStore(state => state.ns);
  const { t } = useTranslate(ns);
  const language = useTolgee(['language']).getLanguage();

  // useTranslate 가 돌려주는 t 의 참조는 ns 가 바뀌어도 같아서, ns 를 직접 넘기고 deps 로 갱신합니다.
  const translate = useCallback<TranslateFn>(
    (key, params) => t(key, { ...params, ns }),
    [t, ns],
  );

  return {
    language: toSupportedLanguage(language),
    t: translate,
  };
};

export const Trans = ({ i18nKey, params }: TransProps) => {
  const ns = useSiteNamespaceStore(state => state.ns);

  return createElement(T, { keyName: i18nKey, ns, params });
};

Trans.displayName = 'Trans';
